import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clapperboard, Image as ImageIcon, Play, Search, X } from 'lucide-react';
import BorderGlow from '../components/BorderGlow';
import PageTitleBlock from '../components/PageTitleBlock';
import { CONTENT_TAXONOMY_SCOPES, getContentTaxonomies } from '../contentTaxonomyApi';
import { videoFirstFrameSource } from '../mediaPreview';
import { getPublishedVideoCases } from './videoCaseApi';
import { useTeamBrand } from '../branding/TeamBrandContext';

function resolveFilter(searchParams, categories) {
  const requested = searchParams.get('category');
  return categories.some(item => item.code === requested) ? requested : '';
}

function LearningCard({ item, onPlay, onOpenProcess }) {
  const brand = useTeamBrand();
  const canViewProcess = Boolean(item.can_view_process && item.canvas_nodes_id);
  const episodeCount = Number(item.episode_count || 0);
  const previewImage = item.cover || item.image_url || '';
  const isVideoCase = Boolean(item.video_url);
  return (
    <article className="group relative min-w-0">
      <button
        type="button"
        aria-label={`${isVideoCase ? '播放' : '查看'}案例：${item.title}`}
        onClick={() => onPlay(item)}
        className="block w-full text-left"
      >
        <div className="relative aspect-[4/3] overflow-hidden rounded-[20px] bg-stone-900 shadow-[0_24px_52px_-42px_rgba(40,31,44,0.7)] ring-1 ring-black/[0.06] transition duration-300 group-hover:shadow-[0_30px_62px_-42px_rgba(40,31,44,0.78)] dark:ring-white/[0.08]">
          {previewImage ? (
            <img src={previewImage} alt={`${item.title}封面`} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.035]" />
          ) : item.video_url ? (
            <video src={videoFirstFrameSource(item.video_url)} muted playsInline preload="metadata" aria-hidden="true" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.035]" />
          ) : (
            <div className="grid h-full place-items-center text-white/28"><Clapperboard size={30} strokeWidth={1.35} /><span className="sr-only">暂无封面</span></div>
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/78" />
          <div className="absolute left-3 top-3 flex items-center gap-2">
            <span className="rounded-full border border-white/15 bg-black/35 px-2.5 py-1 text-[9px] font-medium text-white backdrop-blur-md">{item.category_name || item.category || '案例'}</span>
            {episodeCount > 0 && <span className="rounded-full border border-white/15 bg-black/35 px-2.5 py-1 text-[9px] text-white/75 backdrop-blur-md">{episodeCount} 集</span>}
          </div>
          <span className="absolute left-1/2 top-1/2 grid h-10 w-10 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/20 bg-black/32 text-white opacity-0 backdrop-blur transition group-hover:opacity-100 group-focus-within:opacity-100">{isVideoCase ? <Play size={15} fill="currentColor" /> : <ImageIcon size={15} />}</span>
          <div className="absolute inset-x-0 bottom-0 p-4 text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.75)]">
            <h2 className="line-clamp-2 text-[17px] font-semibold tracking-[-0.025em]">{item.title}</h2>
            <p className="mt-1 truncate text-[10px] text-white/62">{brand.text(item.creator || '小影实验室')} · {item.tag || '精选案例'}</p>
          </div>
        </div>
      </button>
      {canViewProcess && (
        <button
          type="button"
          aria-label={`查看${item.title}的制作过程`}
          onClick={() => onOpenProcess(item)}
          className="absolute right-3 top-3 z-20 inline-flex h-8 items-center gap-1.5 rounded-[9px] border border-white/15 bg-black/48 px-2.5 text-[10px] font-medium text-white shadow-sm backdrop-blur-md transition hover:bg-black/66 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          <Clapperboard size={12} />
          查看制作过程
        </button>
      )}
    </article>
  );
}

export default function LearningPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [categories, setCategories] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const activeFilter = resolveFilter(searchParams, categories);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [cases, taxonomy] = await Promise.all([
        getPublishedVideoCases(),
        getContentTaxonomies(CONTENT_TAXONOMY_SCOPES.videoCases),
      ]);
      setContent(cases);
      setCategories(taxonomy);
    } catch (error) {
      setContent([]);
      setCategories([]);
      setLoadError(getErrorMessage(error, '学习案例加载失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCases(); }, [loadCases]);

  const visibleContent = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase('zh-CN');
    return content.filter(item => {
      if (activeFilter && item.category !== activeFilter) return false;
      if (!keyword) return true;
      return [item.title, item.sub_title, item.category_name, item.category, item.creation_tags, item.tag]
        .join(' ')
        .toLocaleLowerCase('zh-CN')
        .includes(keyword);
    });
  }, [activeFilter, content, query]);

  const chooseFilter = categoryCode => {
    const next = {};
    if (categoryCode) next.category = categoryCode;
    if (query.trim()) next.q = query.trim();
    setSearchParams(next);
  };

  const playCase = item => navigate(`/learn/${encodeURIComponent(item.id)}`);
  const openProcess = item => navigate(`/canvas/${encodeURIComponent(item.canvas_nodes_id)}`);

  return (
    <div className="vnext-page-surface">
      <div className="vnext-page-frame">
        <div className="flex flex-col gap-5 pt-9 sm:pt-0 md:flex-row md:items-end md:justify-between">
          <PageTitleBlock title="学习案例" />
          <BorderGlow edgeSensitivity={30} glowColor="266 42 66" backgroundColor="var(--vnext-composer)" borderRadius={14} glowRadius={28} glowIntensity={0.78} coneSpread={25} animated={false} colors={['#9784c2', '#b88eb9', '#7f99c5']} fillOpacity={0.14} className="w-full md:w-[340px]">
            <label className="flex h-11 w-full items-center gap-2 rounded-[13px] bg-white/72 px-3.5 dark:bg-white/[0.045]">
              <Search size={15} className="text-stone-400" />
              <input value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索案例、题材或方法" aria-label="搜索学习案例" className="min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-stone-400" />
              {query && <button type="button" onClick={() => setQuery('')} aria-label="清空搜索"><X size={14} /></button>}
            </label>
          </BorderGlow>
        </div>

        <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="radiogroup" aria-label="案例分类">
          {[{ code: '', name: '全部' }, ...categories].map(category => {
            const active = category.code === activeFilter;
            return (
              <button type="button" role="radio" aria-checked={active} key={category.code || 'all'} onClick={() => chooseFilter(category.code)} className={`h-9 shrink-0 rounded-full border px-4 text-[12px] font-medium transition ${active ? 'border-[#78668d]/18 bg-[#352f3b] text-white shadow-sm dark:border-transparent dark:bg-[#c1afe3] dark:text-[#241e29]' : 'border-black/[0.07] bg-white/52 text-stone-500 hover:border-[#806b9a]/20 hover:bg-white hover:text-stone-850 dark:border-white/[0.08] dark:bg-white/[0.035] dark:text-white/42 dark:hover:bg-white/[0.07] dark:hover:text-white/75'}`}>
                {category.name}
              </button>
            );
          })}
        </div>

        <section className="mt-6 border-t border-black/[0.06] pt-7 dark:border-white/[0.07]" aria-label="案例列表">
          {loading ? (
            <div className="grid min-h-64 place-items-center text-[12px] text-stone-400">正在加载案例…</div>
          ) : loadError ? (
            <div className="grid min-h-64 place-items-center text-center"><div><h2 className="text-[18px] font-semibold">暂时无法加载内容</h2><p className="mt-2 text-[12px] text-stone-500">{loadError}</p><button type="button" onClick={loadCases} className="mt-4 rounded-full bg-[#352f3b] px-4 py-2 text-[12px] font-medium text-white">重新加载</button></div></div>
          ) : visibleContent.length ? (
            <div className="grid gap-[14px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{visibleContent.map(item => <LearningCard item={item} onPlay={playCase} onOpenProcess={openProcess} key={item.id} />)}</div>
          ) : (
            <div className="grid min-h-64 place-items-center text-center"><div><h2 className="text-[18px] font-semibold">没有找到合适的内容</h2><button type="button" onClick={() => { setQuery(''); setSearchParams({}); }} className="mt-4 rounded-full bg-[#352f3b] px-4 py-2 text-[12px] font-medium text-white">查看全部</button></div></div>
          )}
        </section>
      </div>
    </div>
  );
}
